import React, { useState, useEffect } from "react";
import { Container, Row, Col, Card, CardBody, CardTitle, CardText, Form, FormGroup, Label, Input, Button, Modal, ModalHeader, ModalBody, ModalFooter, Toast, ToastBody } from "reactstrap";
import { useAuth0 } from "@auth0/auth0-react";
import { fetchSuppliers, connectSupplier, syncSupplier, fetchSupplierJobs, pauseSupplier, resumeSupplier, disconnectSupplier, fetchCatalogue, requestSupplier, addCustomSupplier } from "../utils/apiService";
import { useOrg } from "../utils/orgContext";

const Suppliers = ({ uploadTypeOverride, hideStyleExamples }) => {
  const { getAccessTokenSilently } = useAuth0();
  const { orgId, ready } = useOrg();
  const [selectedChoice, setSelectedChoice] = useState(null);
  const [selectedSupplierId, setSelectedSupplierId] = useState(null);
  const [displaySuppliers, setDisplaySuppliers] = useState([]);
  const [visibleCount, setVisibleCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showComingSoonModal, setShowComingSoonModal] = useState(false);
  const [requestedSupplier, setRequestedSupplier] = useState(null);
  const [requesting, setRequesting] = useState(false);
  const [customName, setCustomName] = useState('');
  const [customLoginUrl, setCustomLoginUrl] = useState('');
  const [customError, setCustomError] = useState(null);
  const [addingCustom, setAddingCustom] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [connecting, setConnecting] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [connectError, setConnectError] = useState(null);
  const [jobHistory, setJobHistory] = useState([]);
  const [jobsLoading, setJobsLoading] = useState(false);
  const [toast, setToast] = useState(null); // { message, type: 'success' | 'danger' }
  const [panelTab, setPanelTab] = useState('overview');
  const [showAllAudit, setShowAllAudit] = useState(false);
  const [connType, setConnType] = useState('password'); // 'password' | 'sso' | 'vault'
  const [ssoProvider, setSsoProvider] = useState('');
  const [mfaEnabled, setMfaEnabled] = useState(false);
  const [mfaType, setMfaType] = useState('totp'); // 'totp' | 'sms' | 'email' | 'hardware'
  const [mfaCode, setMfaCode] = useState('');
  const [selectedOrgSso, setSelectedOrgSso] = useState(null);
  const [showManualSso, setShowManualSso] = useState(false);
  const [syncMode, setSyncMode] = useState('simple'); // 'simple' | 'cron'
  const [syncInterval, setSyncInterval] = useState(1);
  const [syncUnit, setSyncUnit] = useState('weeks'); // 'days' | 'weeks' | 'months'
  const [syncCron, setSyncCron] = useState('');
  const [jobsVersion, setJobsVersion] = useState(0);
  const [changingConnection, setChangingConnection] = useState(false);

  // Mocked org-wide SSO configurations — will come from API
  const orgSsoConfigs = [
    { id: 1, name: 'Acme Corp — Okta', provider: 'Okta', domain: 'acmecorp.com', providerDomain: 'okta.com', status: 'active' },
    { id: 2, name: 'Acme Corp — Azure AD', provider: 'Azure AD', domain: 'acmecorp.com', providerDomain: 'microsoft.com', status: 'active' },
    { id: 3, name: 'Dev Team — Google', provider: 'Google', domain: 'dev.acmecorp.com', providerDomain: 'workspace.google.com', status: 'inactive' },
  ];

  const showToast = (message, type = 'success') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 3000);
  };

  const loadSuppliers = async () => {
    setLoading(true);
    setDisplaySuppliers([]);
    setVisibleCount(0);
    setError(null);

    let token = null;
    try { token = await getAccessTokenSilently(); } catch (e) {}

    const [suppliersResult, catalogueResult] = await Promise.allSettled([
      fetchSuppliers(token, orgId),
      fetchCatalogue(token),
    ]);

    const suppliersData = suppliersResult.status === 'fulfilled' ? suppliersResult.value : null;
    if (suppliersResult.status === 'rejected') setError(suppliersResult.reason?.message || 'Could not load suppliers');

    const apiSuppliers = Array.isArray(suppliersData?.values) ? suppliersData.values : [];
    const supported = apiSuppliers.map((s, i) => ({
      id: s.Id || s.id || i + 1,
      title: (s.name || '').replace(/\s+Connection$/i, ''),
      description: 'Setup Login',
      icon: s.iconUrl || '',
      serviceId: s.serviceId,
      activationState: s.ActivationState || s.activationState,
      metaDataList: s.metaDataList || [],
      isFromApi: true,
    }));

    const comingSoonList = catalogueResult.status === 'fulfilled' ? (catalogueResult.value?.comingSoon || []) : [];
    const comingSoon = comingSoonList.map((c, i) => ({
      id: `soon-${i}`,
      title: c.name,
      description: 'Coming soon',
      icon: c.iconUrl || '',
      comingSoon: true,
    }));

    const all = [...supported, ...comingSoon];
    setLoading(false);
    setDisplaySuppliers(all);
    all.forEach((_, i) => {
      setTimeout(() => setVisibleCount(i + 1), Math.min(i, 40) * 30);
    });
  };

  useEffect(() => {
    if (ready) loadSuppliers();
    // loadSuppliers reads orgId; reload whenever the selected org changes
  }, [getAccessTokenSilently, ready, orgId]);

  // Load job history when a supplier is selected
  useEffect(() => {
    if (selectedSupplierId) {
      const loadJobs = async () => {
        setJobsLoading(true);
        setJobHistory([]);
        try {
          let token = null;
          try { token = await getAccessTokenSilently(); } catch (e) {}
          const data = await fetchSupplierJobs(selectedSupplierId, token, orgId);
          const jobs = Array.isArray(data) ? data : (data?.values || data?.jobs || []);
          setJobHistory(jobs);
        } catch (e) {
          setJobHistory([]);
        } finally {
          setJobsLoading(false);
        }
      };
      loadJobs();
    } else {
      setJobHistory([]);
    }
  }, [selectedSupplierId, getAccessTokenSilently, orgId, jobsVersion]);

  // Populate email and password fields from metadata when supplier is selected
  useEffect(() => {
    if (selectedSupplierId) {
      const supplier = displaySuppliers.find(s => s.id === selectedSupplierId);
      if (supplier && supplier.metaDataList && Array.isArray(supplier.metaDataList)) {
        const emailMeta = supplier.metaDataList.find(
          meta => meta.metaDataIdentifier === 'supplier_email'
        );
        const passwordMeta = supplier.metaDataList.find(
          meta => meta.metaDataIdentifier === 'supplier_password'
        );
        
        if (emailMeta && emailMeta.metaData) {
          setEmail(emailMeta.metaData);
        }
        
        if (passwordMeta && passwordMeta.metaData) {
          setPassword(passwordMeta.metaData);
        } else {
          setPassword('');
        }
      } else {
        // Reset fields if no metadata
        setEmail('');
        setPassword('');
      }
    }
  }, [selectedSupplierId, displaySuppliers]);

  const handleChoiceSelect = (choiceId) => {
    const picked = displaySuppliers.find(s => s.id === choiceId);
    if (picked?.comingSoon) {
      setRequestedSupplier(picked);
      setCustomName(picked.title);
      setCustomLoginUrl('');
      setCustomError(null);
      setShowComingSoonModal(true);
      return;
    }
    setSelectedSupplierId(choiceId);
    setSelectedChoice(null);
    setPanelTab('overview');
    setShowAllAudit(false);
    setConnType('password');
    setSsoProvider('');
    setMfaEnabled(false);
    setMfaType('totp');
    setMfaCode('');
    setSelectedOrgSso(null);
    setShowManualSso(false);
  };

  const handleConnect = async () => {
    const supplier = displaySuppliers.find(s => s.id === selectedSupplierId);
    if (!supplier) {
      setConnectError('Supplier not found');
      return;
    }

    if (!email || !password) {
      setConnectError('Please enter both email and password');
      return;
    }

    setConnecting(true);
    setConnectError(null);

    try {
      let token = null;
      try {
        token = await getAccessTokenSilently();
      } catch (e) {
        console.log('No access token available:', e);
      }

      const connectionData = {
        emailAddress: email,
        password,
        serviceId: supplier.serviceId,
        Id: supplier.id,
        scheduleMode: syncMode,
        scheduleInterval: syncInterval,
        scheduleUnit: syncUnit,
        scheduleCron: syncCron
      };

      await connectSupplier(connectionData, token, orgId);

      setSelectedSupplierId(null);
      setEmail('');
      setPassword('');
      showToast('Connection saved successfully');
      await loadSuppliers();
    } catch (error) {
      console.error('Error connecting supplier:', error);
      showToast(error.message || 'Failed to connect supplier', 'danger');
    } finally {
      setConnecting(false);
    }
  };

  const isPaused = (supplier) => {
    const entries = (supplier?.metaDataList || []).filter(m => m.metaDataIdentifier === 'sync_paused');
    return entries.length > 0 && entries[entries.length - 1].metaData === 'true';
  };

  const handleConnectionChange = async (action, supplier) => {
    if (!supplier) return;
    if (action === 'disconnect' && !window.confirm(`Disconnect ${supplier.name}? Its saved login is removed; receipts already collected stay.`)) return;
    setChangingConnection(true);
    try {
      let token = null;
      try { token = await getAccessTokenSilently(); } catch (e) {}
      const call = action === 'pause' ? pauseSupplier : action === 'resume' ? resumeSupplier : disconnectSupplier;
      await call(supplier, token, orgId);
      showToast(action === 'pause' ? 'Syncing paused' : action === 'resume' ? 'Syncing resumed' : 'Supplier disconnected');
      if (action === 'disconnect') setSelectedSupplierId(null);
      await loadSuppliers();
      setJobsVersion(v => v + 1);
    } catch (error) {
      showToast('Could not update the connection. Please try again.', 'danger');
    } finally {
      setChangingConnection(false);
    }
  };

  const handleAddCustom = async () => {
    setAddingCustom(true);
    setCustomError(null);
    try {
      let token = null;
      try { token = await getAccessTokenSilently(); } catch (e) {}
      const result = await addCustomSupplier(customName, customLoginUrl, token, orgId);
      setShowComingSoonModal(false);
      showToast(result?.Existing ? `${customName} is already set up` : `${customName} added. Enter its login to connect.`);
      await loadSuppliers();
      if (result?.Id) handleChoiceSelect(result.Id);
    } catch (e) {
      setCustomError(e.userMessage || 'The supplier could not be added. Please try again.');
    } finally {
      setAddingCustom(false);
    }
  };

  const handleRequestSupplier = async () => {
    if (!requestedSupplier) return;
    setRequesting(true);
    try {
      let token = null;
      try { token = await getAccessTokenSilently(); } catch (e) {}
      await requestSupplier(requestedSupplier.title, token, orgId);
      setShowComingSoonModal(false);
      showToast(`Thanks, we've noted your request for ${requestedSupplier.title}`);
    } catch (e) {
      showToast('Could not send the request. Please try again.', 'danger');
    } finally {
      setRequesting(false);
    }
  };

  const handleSync = async (supplierId = null) => {
    const idToUse = supplierId || selectedSupplierId;
    const supplier = displaySuppliers.find(s => s.id === idToUse);
    if (!supplier) {
      setConnectError('Supplier not found');
      return;
    }

    setSyncing(true);
    setConnectError(null);

    try {
      let token = null;
      try {
        token = await getAccessTokenSilently();
      } catch (e) {
        console.log('No access token available:', e);
      }

      const syncData = {
        serviceId: supplier.serviceId,
        Id: supplier.id,
        ...(mfaEnabled && mfaCode ? { mfaCode } : {})
      };

      setJobHistory(prev => [{ status: 'pending', createdAt: new Date().toISOString() }, ...prev]);

      await syncSupplier(syncData, token, orgId);
      setMfaCode('');
      showToast('Sync queued successfully');
      setJobsVersion(v => v + 1);
    } catch (error) {
      console.error('Error syncing supplier:', error);
      showToast(error.message || 'Failed to sync supplier', 'danger');
    } finally {
      setSyncing(false);
    }
  };

  const renderQuestions = () => {
    if (!selectedChoice) return null;

    switch (selectedChoice) {
      // case 1: // ChatGPT
      //   return (
      //     <Card className="mt-4">
      //       <CardBody>
      //         <CardTitle tag="h4" className="mb-3">ChatGPT Configuration</CardTitle>
      //         <Form>
      //           <Row>
      //             <Col md={6}>
      //               <FormGroup>
      //                 <Label for="height">Height (cm)</Label>
      //                 <Input type="number" id="height" placeholder="Enter your height" />
      //               </FormGroup>
      //             </Col>
      //             <Col md={6}>
      //               <FormGroup>
      //                 <Label for="weight">Weight (kg)</Label>
      //                 <Input type="number" id="weight" placeholder="Enter your weight" />
      //               </FormGroup>
      //             </Col>
      //           </Row>
      //           <FormGroup>
      //             <Label for="bodyType">Body Type</Label>
      //             <Input type="select" id="bodyType">
      //               <option>Select body type</option>
      //               <option>Slim</option>
      //               <option>Athletic</option>
      //               <option>Average</option>
      //               <option>Curvy</option>
      //             </Input>
      //           </FormGroup>
      //           <FormGroup>
      //             <Label for="selfieUpload">Upload Selfie</Label>
      //             <Input type="file" id="selfieUpload" accept="image/*" />
      //           </FormGroup>
      //           <Button color="primary" className="mt-3">Continue</Button>
      //         </Form>
      //       </CardBody>
      //     </Card>
      //   );

      default:
        return null;
    }
  };

  return (
    <>
      <div style={{ position: 'relative', paddingBottom: '3rem' }}>
          <div className="mb-4">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '1rem' }}>
              <div>
                <h2 className="page-title mb-1">Suppliers</h2>
                <p className="page-subtitle">Connect and manage your data sources</p>
              </div>
              <button
                className="btn-pill btn-pill-blue"
                onClick={() => { setRequestedSupplier(null); setCustomName(''); setCustomLoginUrl(''); setCustomError(null); setShowComingSoonModal(true); }}
              >
                Add your own supplier
              </button>
            </div>
          </div>
          
          {error && <div className="text-center mb-4 text-danger">Error loading suppliers: {error}</div>}

          <style>{`
            @keyframes supplierFadeIn {
              from { opacity: 0; transform: translateY(8px) scale(0.95); }
              to { opacity: 1; transform: translateY(0) scale(1); }
            }
            .supplier-card-hidden { opacity: 0; pointer-events: none; }
            .supplier-card-visible { animation: supplierFadeIn 0.22s cubic-bezier(0.34, 1.2, 0.64, 1) forwards; }
          `}</style>

          {loading && displaySuppliers.length === 0 && (
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '4rem 0', color: '#adb5bd' }}>
              <div className="spinner-border text-primary mb-3" style={{ width: '2rem', height: '2rem' }} role="status" />
              <div style={{ fontSize: '0.875rem' }}>Loading suppliers...</div>
            </div>
          )}

          <Row>
            {displaySuppliers.map((choice, index) => (
              <Col key={`supplier-${choice.id}-${choice.isFromApi ? 'api' : 'hardcoded'}-${index}`} xs={4} sm={6} md={4} className="mb-3">
                <Card
                  className={`choice-card ${selectedChoice === choice.id ? 'selected' : ''} ${index < visibleCount ? 'supplier-card-visible' : 'supplier-card-hidden'}`}
                  style={{ position: 'relative', minHeight: '130px', cursor: 'pointer' }}
                  onClick={() => handleChoiceSelect(choice.id)}
                >
                  {/* Activation State Dot */}
                  {choice.activationState && (
                    <div style={{ position: 'absolute', top: '10px', right: '10px', zIndex: 10 }}>
                      <div style={{
                        width: '8px',
                        height: '8px',
                        borderRadius: '50%',
                        backgroundColor: choice.activationState === 2 ? 'var(--color-success)' : 'var(--color-danger)',
                      }} />
                    </div>
                  )}
                  <CardBody className="text-center" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '1.25rem 0.75rem' }}>
                    <div className="choice-icon mb-2" style={{ height: '2.5rem', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      {choice.icon && (choice.icon.startsWith('/') || choice.icon.startsWith('http')) ? (
                        <img src={choice.icon} alt={choice.title} style={{ maxWidth: '100%', maxHeight: '2.5rem', objectFit: 'contain' }} />
                      ) : choice.icon ? (
                        <span style={{ fontSize: '1.5rem' }}>{choice.icon}</span>
                      ) : null}
                    </div>
                    <CardTitle tag="h6" className="mb-0" style={{ fontSize: '0.82rem', fontWeight: 600, color: '#343a40' }}>
                      {choice.title}
                    </CardTitle>
                    {(choice.description === "Setup Login" || choice.activationState === 2) && (
                      <div className="mt-3 d-none d-sm-block">
                        <span className={`btn-pill ${choice.activationState === 2 ? 'btn-pill-blue' : 'btn-pill-green'}`}>
                          {choice.activationState === 2 ? "Manage" : "Connect"}
                        </span>
                      </div>
                    )}
                  </CardBody>
                </Card>
              </Col>
            ))}
          </Row>

          {/* Mobile-only Dynamic Caption Text */}
          <div className="text-center mt-3 mb-4 d-block d-sm-none">
            <p className="text-muted small">
              {/* {selectedChoice === 1 && "Upload a selfie and provide body measurements for the most accurate fit recommendations."} */}
            </p>
          </div>

          {/* Dynamic Questions Section */}
          <div style={{ position: 'relative' }}>
            {renderQuestions()}
          </div>
      </div>
    
    {/* Right Side Menu when a supplier is selected */}
    {selectedSupplierId != null && (() => {
      const supplier = displaySuppliers.find(s => s.id === selectedSupplierId);
      const supplierName = supplier?.title || supplier?.name || 'LinkedIn';
      const displayName = supplierName.replace("Connection", "");
      
      return (
        <div
          style={{
            position: 'fixed',
            top: 0,
            right: 0,
            width: '380px',
            height: '100vh',
            backgroundColor: '#fff',
            boxShadow: '-4px 0 24px rgba(0,0,0,0.08)',
            zIndex: 1050,
            overflowY: 'auto',
            transition: 'transform 0.3s ease-in-out'
          }}
        >
          {/* Header */}
          <div style={{ padding: '1.5rem 1.75rem 0' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.25rem' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                {supplier?.icon && (
                  <img src={supplier.icon} alt={displayName} style={{ width: '32px', height: '32px', objectFit: 'contain', borderRadius: '6px' }} />
                )}
                <div>
                  <div style={{ fontSize: '0.68rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em', color: '#adb5bd', marginBottom: '1px' }}>Supplier</div>
                  <h5 style={{ margin: 0, fontWeight: 700, color: '#1a1d23', fontSize: '1rem' }}>{displayName}</h5>
                </div>
              </div>
              <Button
                color="link"
                onClick={() => { setSelectedSupplierId(null); setEmail(''); setPassword(''); setConnectError(null); }}
                style={{ padding: '4px 8px', fontSize: '1.2rem', lineHeight: 1, color: '#adb5bd' }}
              >×</Button>
            </div>

            {/* Status pill */}
            <div style={{ marginBottom: '1.25rem' }}>
              <span style={{
                display: 'inline-flex', alignItems: 'center', gap: '5px',
                fontSize: '0.7rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em',
                padding: '3px 10px', borderRadius: '20px',
                background: supplier?.activationState === 2 ? '#d1f5e0' : '#f0f0f0',
                color: supplier?.activationState === 2 ? '#1a7f47' : '#888',
              }}>
                <span style={{ width: 6, height: 6, borderRadius: '50%', background: supplier?.activationState === 2 ? '#28a745' : '#ccc', display: 'inline-block' }} />
                {supplier?.activationState === 2 ? 'Connected' : 'Not connected'}
              </span>
            </div>

            {/* Tabs */}
            <div style={{ display: 'flex', borderBottom: '2px solid #f0f0f0', marginLeft: '-1.75rem', marginRight: '-1.75rem', paddingLeft: '1.75rem', gap: '0' }}>
              {[
                { key: 'overview', label: 'Overview' },
                { key: 'configuration', label: 'Configuration' },
                { key: 'workflow', label: 'Workflow' },
              ].map(({ key, label }) => (
                <button
                  key={key}
                  onClick={() => setPanelTab(key)}
                  style={{
                    background: 'none', border: 'none', padding: '0.6rem 1rem',
                    fontSize: '0.78rem', fontWeight: 600, cursor: 'pointer',
                    color: panelTab === key ? 'var(--color-primary, #5b8dee)' : '#adb5bd',
                    borderBottom: panelTab === key ? '2px solid var(--color-primary, #5b8dee)' : '2px solid transparent',
                    marginBottom: '-2px', letterSpacing: '0.02em', whiteSpace: 'nowrap',
                  }}
                >
                  {label}
                </button>
              ))}
            </div>
          </div>

          {/* Tab content */}
          <div style={{ padding: '1.5rem 1.75rem' }}>

            {/* ── OVERVIEW ── */}
            {panelTab === 'overview' && (() => {
              const isConnected = supplier?.activationState === 2;
              const lastRun = jobHistory[0];
              const lastRunDate = lastRun ? (lastRun.createdAt || lastRun.CreatedAt || lastRun.runAt || lastRun.RunAt) : null;
              const lastRunStatus = lastRun ? (lastRun.status || lastRun.Status || lastRun.state || lastRun.State) : null;
              const lastRunOk = lastRunStatus === 2 || lastRunStatus === 'success' || lastRunStatus === 'Success' || lastRunStatus === 'completed';
              return (
                <div>
                  {/* Hero banner */}
                  <div style={{
                    borderRadius: '14px',
                    background: isConnected
                      ? 'linear-gradient(135deg, #edf5ff 0%, #f0fff6 100%)'
                      : 'linear-gradient(135deg, #f7f8fa 0%, #f0f0f0 100%)',
                    border: `1px solid ${isConnected ? '#c8e6ff' : '#e9ecef'}`,
                    padding: '1.5rem',
                    marginBottom: '1.25rem',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    textAlign: 'center',
                    gap: '0.75rem',
                  }}>
                    {supplier?.icon && (
                      <div style={{
                        width: '64px', height: '64px', borderRadius: '16px',
                        background: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center',
                        boxShadow: '0 2px 12px rgba(0,0,0,0.08)',
                        padding: '10px',
                      }}>
                        <img src={supplier.icon} alt={displayName} style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
                      </div>
                    )}
                    <div>
                      <div style={{ fontWeight: 700, fontSize: '1.1rem', color: '#1a1d23', marginBottom: '4px' }}>{displayName}</div>
                      <div style={{ fontSize: '0.75rem', color: '#6c757d' }}>
                        {supplier?.description || (isConnected ? 'Connected and syncing' : 'Not yet connected')}
                      </div>
                    </div>
                    <div style={{
                      display: 'inline-flex', alignItems: 'center', gap: '6px',
                      padding: '5px 14px', borderRadius: '20px',
                      background: isConnected ? '#d1f5e0' : '#f0f0f0',
                      color: isConnected ? '#1a7f47' : '#888',
                      fontSize: '0.72rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em',
                    }}>
                      <span style={{ width: 7, height: 7, borderRadius: '50%', background: isConnected ? '#28a745' : '#ccc', display: 'inline-block' }} />
                      {isConnected ? 'Connected' : 'Not connected'}
                    </div>
                  </div>

                  {/* Stat tiles */}
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.65rem', marginBottom: '1.25rem' }}>
                    {[
                      {
                        label: 'Last Sync',
                        value: lastRunDate ? new Date(lastRunDate).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' }) : '—',
                        sub: lastRunDate ? new Date(lastRunDate).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' }) : 'Never synced',
                        accent: lastRun ? (lastRunOk ? '#28a745' : '#dc3545') : '#adb5bd',
                      },
                      {
                        label: 'Total Runs',
                        value: jobsLoading ? '…' : jobHistory.length,
                        sub: jobHistory.filter(j => { const s = j.status||j.Status||j.state||j.State; return s===2||s==='success'||s==='Success'||s==='completed'; }).length + ' succeeded',
                        accent: '#5b8dee',
                      },
                      {
                        label: 'Type',
                        value: supplier?.isFromApi ? 'API' : 'Manual',
                        sub: supplier?.isFromApi ? 'Live integration' : 'Manual import',
                        accent: '#9b7fe8',
                      },
                      {
                        label: 'Receipts',
                        value: jobsLoading ? '—' : jobHistory.reduce((n, j) => n + (j.receiptsUploaded || 0), 0),
                        sub: 'collected by syncs',
                        accent: '#e8a84d',
                      },
                    ].map(({ label, value, sub, accent }) => (
                      <div key={label} style={{
                        background: '#f8f9fa', borderRadius: '12px', padding: '0.9rem',
                        border: '1px solid #f0f0f0',
                      }}>
                        <div style={{ fontSize: '0.65rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em', color: '#adb5bd', marginBottom: '4px' }}>{label}</div>
                        <div style={{ fontSize: '1.25rem', fontWeight: 700, color: accent, lineHeight: 1, marginBottom: '3px' }}>{value}</div>
                        <div style={{ fontSize: '0.68rem', color: '#adb5bd' }}>{sub}</div>
                      </div>
                    ))}
                  </div>

                  {/* Audit log placeholder - moved below actions */}
                  <div style={{ display: 'none' }}>
                    <div style={{ fontSize: '0.7rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em', color: '#adb5bd', marginBottom: '0.6rem' }}>Audit Log</div>
                    {jobsLoading ? (
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', color: '#adb5bd', fontSize: '0.78rem', padding: '0.75rem 0' }}>
                        <div className="spinner-border spinner-border-sm" role="status" />
                        <span>Loading...</span>
                      </div>
                    ) : jobHistory.length === 0 ? (
                      <div style={{ padding: '1rem', background: '#f8f9fa', borderRadius: '10px', border: '1px dashed #dee2e6', textAlign: 'center' }}>
                        <div style={{ fontSize: '1.25rem', marginBottom: '0.35rem' }}>📋</div>
                        <div style={{ fontSize: '0.75rem', color: '#adb5bd' }}>No sync history yet</div>
                      </div>
                    ) : (
                      <>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem' }}>
                          {(showAllAudit ? jobHistory : jobHistory.slice(0, 3)).map((job, i) => {
                            const s = job.status||job.Status||job.state||job.State;
                            const ok = s==='success'||s==='Success'||s==='completed';
                            const needsMfa = s==='needs_mfa';
                            const fail = needsMfa||s==='failed'||s==='Failed'||s==='error';
                            const d = job.createdAt||job.CreatedAt||job.runAt||job.RunAt;
                            return (
                              <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', padding: '0.5rem 0.7rem', borderRadius: '8px', background: '#fff', border: '1px solid #f0f0f0' }}>
                                <div style={{ width: 28, height: 28, borderRadius: '50%', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.75rem',
                                  background: ok ? '#d1f5e0' : fail ? '#fde8e8' : '#fff3cd',
                                  color: ok ? '#1a7f47' : fail ? '#c0392b' : '#856404',
                                }}>
                                  {ok ? '✓' : fail ? '✕' : '⟳'}
                                </div>
                                <div style={{ flex: 1, minWidth: 0 }}>
                                  <div style={{ fontSize: '0.75rem', fontWeight: 600, color: '#343a40' }}>{ok ? 'Sync succeeded' : needsMfa ? 'Needs a verification code' : fail ? 'Sync failed' : s==='running' ? 'Running' : 'Queued'}</div>
                                  {d && <div style={{ fontSize: '0.68rem', color: '#adb5bd' }}>{new Date(d).toLocaleString('en-GB', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}</div>}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                        {jobHistory.length > 3 && (
                          <button
                            onClick={() => setShowAllAudit(v => !v)}
                            style={{ background: 'none', border: 'none', padding: '0.4rem 0', fontSize: '0.72rem', color: 'var(--color-primary, #5b8dee)', cursor: 'pointer', fontWeight: 600 }}
                          >
                            {showAllAudit ? 'Show less ↑' : `View all ${jobHistory.length} events ↓`}
                          </button>
                        )}
                      </>
                    )}
                  </div>

                  {/* Actions */}
                  <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '1.5rem' }}>
                    <button
                      className="btn-pill btn-pill-blue"
                      style={{ flex: 1, justifyContent: 'center', padding: '0.6rem 1rem', fontSize: '0.82rem', borderRadius: '8px' }}
                      onClick={() => setPanelTab('configuration')}
                    >
                      {isConnected ? 'Edit config' : 'Connect'}
                    </button>
                    {isConnected && (
                      <button
                        className="btn-pill btn-pill-green"
                        style={{ flex: 1, justifyContent: 'center', padding: '0.6rem 1rem', fontSize: '0.82rem', borderRadius: '8px' }}
                        onClick={() => handleSync()}
                        disabled={syncing}
                      >
                        {syncing ? 'Syncing…' : 'Sync now'}
                      </button>
                    )}
                    {isConnected && (
                      <button
                        className="btn-pill"
                        style={{ flex: 1, justifyContent: 'center', padding: '0.6rem 1rem', fontSize: '0.82rem', borderRadius: '8px' }}
                        onClick={() => handleConnectionChange(isPaused(supplier) ? 'resume' : 'pause', supplier)}
                        disabled={changingConnection}
                      >
                        {isPaused(supplier) ? 'Resume' : 'Pause'}
                      </button>
                    )}
                    {isConnected && (
                      <button
                        className="btn-pill btn-pill-red"
                        style={{ flex: 1, justifyContent: 'center', padding: '0.6rem 1rem', fontSize: '0.82rem', borderRadius: '8px' }}
                        onClick={() => handleConnectionChange('disconnect', supplier)}
                        disabled={changingConnection}
                      >
                        Disconnect
                      </button>
                    )}
                  </div>

                  {/* Audit log — below actions */}
                  <div>
                    <div style={{ fontSize: '0.7rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em', color: '#adb5bd', marginBottom: '0.6rem' }}>Audit Log</div>
                    {jobsLoading ? (
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', color: '#adb5bd', fontSize: '0.78rem', padding: '0.75rem 0' }}>
                        <div className="spinner-border spinner-border-sm" role="status" />
                        <span>Loading...</span>
                      </div>
                    ) : jobHistory.length === 0 ? (
                      <div style={{ padding: '1rem', background: '#f8f9fa', borderRadius: '10px', border: '1px dashed #dee2e6', textAlign: 'center' }}>
                        <div style={{ fontSize: '1.25rem', marginBottom: '0.35rem' }}>📋</div>
                        <div style={{ fontSize: '0.75rem', color: '#adb5bd' }}>No sync history yet</div>
                      </div>
                    ) : (
                      <>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem' }}>
                          {(showAllAudit ? jobHistory : jobHistory.slice(0, 3)).map((job, i) => {
                            const s = job.status||job.Status||job.state||job.State;
                            const ok = s==='success'||s==='Success'||s==='completed';
                            const needsMfa = s==='needs_mfa';
                            const fail = needsMfa||s==='failed'||s==='Failed'||s==='error';
                            const d = job.createdAt||job.CreatedAt||job.runAt||job.RunAt;
                            return (
                              <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', padding: '0.5rem 0.7rem', borderRadius: '8px', background: '#fff', border: '1px solid #f0f0f0' }}>
                                <div style={{ width: 28, height: 28, borderRadius: '50%', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.75rem',
                                  background: ok ? '#d1f5e0' : fail ? '#fde8e8' : '#fff3cd',
                                  color: ok ? '#1a7f47' : fail ? '#c0392b' : '#856404',
                                }}>
                                  {ok ? '✓' : fail ? '✕' : '⟳'}
                                </div>
                                <div style={{ flex: 1, minWidth: 0 }}>
                                  <div style={{ fontSize: '0.75rem', fontWeight: 600, color: '#343a40' }}>{ok ? 'Sync succeeded' : needsMfa ? 'Needs a verification code' : fail ? 'Sync failed' : s==='running' ? 'Running' : 'Queued'}</div>
                                  {d && <div style={{ fontSize: '0.68rem', color: '#adb5bd' }}>{new Date(d).toLocaleString('en-GB', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}</div>}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                        {jobHistory.length > 3 && (
                          <button onClick={() => setShowAllAudit(v => !v)} style={{ background: 'none', border: 'none', padding: '0.4rem 0', fontSize: '0.72rem', color: 'var(--color-primary, #5b8dee)', cursor: 'pointer', fontWeight: 600 }}>
                            {showAllAudit ? 'Show less ↑' : `View all ${jobHistory.length} events ↓`}
                          </button>
                        )}
                      </>
                    )}
                  </div>
                </div>
              );
            })()}

            {/* ── CONFIGURATION ── */}
            {panelTab === 'configuration' && (
              <div>
                {connectError && <div className="alert alert-danger mb-3">{connectError}</div>}

                {/* Connection type selector */}
                <div style={{ marginBottom: '1.5rem' }}>
                  <div style={{ fontSize: '0.7rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em', color: '#adb5bd', marginBottom: '0.6rem' }}>
                    Connection Method
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                    {[
                      { key: 'password', label: 'Username & Password', desc: 'Sign in with your account credentials', icon: '🔑' },
                      { key: 'sso', label: 'Single Sign-On (SSO)', desc: 'Authenticate via your identity provider', icon: '🏢' },
                      { key: 'vault', label: 'Password Manager', desc: '1Password, Bitwarden, LastPass and more', icon: '🔒' },
                    ].map(({ key, label, desc, icon }) => (
                      <button
                        key={key}
                        onClick={() => setConnType(key)}
                        style={{
                          display: 'flex', alignItems: 'center', gap: '0.75rem',
                          padding: '0.75rem 1rem', borderRadius: '10px', cursor: 'pointer',
                          border: connType === key ? '2px solid var(--color-primary, #5b8dee)' : '2px solid #e9ecef',
                          background: connType === key ? '#f0f5ff' : '#f8f9fa',
                          textAlign: 'left', width: '100%',
                          transition: 'border-color 0.15s, background 0.15s',
                        }}
                      >
                        <span style={{ fontSize: '1.3rem', flexShrink: 0 }}>{icon}</span>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontSize: '0.82rem', fontWeight: 600, color: connType === key ? 'var(--color-primary, #5b8dee)' : '#343a40' }}>{label}</div>
                          <div style={{ fontSize: '0.7rem', color: '#adb5bd', marginTop: '1px' }}>{desc}</div>
                        </div>
                        <div style={{
                          width: 18, height: 18, borderRadius: '50%', flexShrink: 0,
                          border: connType === key ? '5px solid var(--color-primary, #5b8dee)' : '2px solid #dee2e6',
                          background: '#fff',
                        }} />
                      </button>
                    ))}
                  </div>
                </div>

                {/* Divider */}
                <div style={{ height: 1, background: '#f0f0f0', marginBottom: '1.25rem' }} />

                {/* ── Username & Password ── */}
                {connType === 'password' && (
                  <Form>
                    <FormGroup>
                      <Label for="supplierEmail">{displayName} Email</Label>
                      <Input
                        type="email" id="supplierEmail"
                        placeholder={`Enter your ${displayName} email`}
                        value={email} onChange={(e) => setEmail(e.target.value)}
                        disabled={connecting}
                      />
                    </FormGroup>
                    <FormGroup>
                      <Label for="supplierPassword">Password</Label>
                      <Input
                        type="password" id="supplierPassword"
                        placeholder="Enter your password"
                        value={password} onChange={(e) => setPassword(e.target.value)}
                        disabled={connecting || syncing}
                      />
                    </FormGroup>

                    {/* MFA toggle */}
                    <div style={{ marginBottom: '1.25rem' }}>
                      <button
                        type="button"
                        onClick={() => setMfaEnabled(v => !v)}
                        style={{
                          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                          width: '100%', padding: '0.7rem 0.9rem', borderRadius: '10px', cursor: 'pointer',
                          border: mfaEnabled ? '2px solid var(--color-primary, #5b8dee)' : '2px solid #e9ecef',
                          background: mfaEnabled ? '#f0f5ff' : '#f8f9fa',
                          transition: 'all 0.15s',
                        }}
                      >
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.65rem' }}>
                          <span style={{ fontSize: '1.1rem' }}>🛡️</span>
                          <div style={{ textAlign: 'left' }}>
                            <div style={{ fontSize: '0.82rem', fontWeight: 600, color: mfaEnabled ? 'var(--color-primary, #5b8dee)' : '#343a40' }}>
                              Multi-Factor Authentication
                            </div>
                            <div style={{ fontSize: '0.7rem', color: '#adb5bd', marginTop: '1px' }}>
                              Required for this account
                            </div>
                          </div>
                        </div>
                        {/* Toggle pill */}
                        <div style={{
                          width: 36, height: 20, borderRadius: 10, flexShrink: 0,
                          background: mfaEnabled ? 'var(--color-primary, #5b8dee)' : '#dee2e6',
                          position: 'relative', transition: 'background 0.2s',
                        }}>
                          <div style={{
                            width: 14, height: 14, borderRadius: '50%', background: '#fff',
                            position: 'absolute', top: 3,
                            left: mfaEnabled ? 19 : 3,
                            transition: 'left 0.2s',
                            boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
                          }} />
                        </div>
                      </button>

                      {/* MFA options - expand when toggled on */}
                      {mfaEnabled && (
                        <div style={{ marginTop: '0.75rem', padding: '1rem', background: '#f8f9fa', borderRadius: '10px', border: '1px solid #e9ecef' }}>
                          <div style={{ fontSize: '0.68rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em', color: '#adb5bd', marginBottom: '0.6rem' }}>
                            MFA Method
                          </div>
                          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.4rem', marginBottom: '0.85rem' }}>
                            {[
                              { key: 'totp', label: 'Authenticator', icon: '📱', desc: 'Google Auth, Authy…' },
                              { key: 'sms', label: 'SMS Code', icon: '💬', desc: 'Text message OTP' },
                              { key: 'email', label: 'Email Code', icon: '📧', desc: 'One-time code' },
                              { key: 'hardware', label: 'Hardware Key', icon: '🔐', desc: 'YubiKey, FIDO2' },
                            ].map(({ key, label, icon, desc }) => (
                              <button
                                key={key}
                                type="button"
                                onClick={() => setMfaType(key)}
                                style={{
                                  display: 'flex', flexDirection: 'column', alignItems: 'flex-start',
                                  gap: '2px', padding: '0.55rem 0.6rem', borderRadius: '8px', cursor: 'pointer',
                                  border: mfaType === key ? '2px solid var(--color-primary, #5b8dee)' : '2px solid #e9ecef',
                                  background: mfaType === key ? '#f0f5ff' : '#fff',
                                  transition: 'all 0.15s', textAlign: 'left',
                                }}
                              >
                                <span style={{ fontSize: '1rem' }}>{icon}</span>
                                <span style={{ fontSize: '0.72rem', fontWeight: 600, color: mfaType === key ? 'var(--color-primary, #5b8dee)' : '#343a40' }}>{label}</span>
                                <span style={{ fontSize: '0.62rem', color: '#adb5bd' }}>{desc}</span>
                              </button>
                            ))}
                          </div>

                          {(mfaType === 'totp' || mfaType === 'sms' || mfaType === 'email') && (
                            <FormGroup style={{ marginBottom: 0 }}>
                              <Label style={{ fontSize: '0.75rem', fontWeight: 600, color: '#495057' }}>
                                {mfaType === 'totp' ? 'One-Time Code' : mfaType === 'sms' ? 'SMS Code' : 'Email Code'}
                              </Label>
                              <Input
                                type="text"
                                placeholder="Enter 6-digit code"
                                value={mfaCode}
                                onChange={e => setMfaCode(e.target.value)}
                                maxLength={8}
                                style={{ letterSpacing: '0.2em', fontWeight: 600, fontSize: '1rem', textAlign: 'center' }}
                              />
                            </FormGroup>
                          )}
                          {mfaType === 'hardware' && (
                            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.6rem 0.75rem', borderRadius: '8px', background: '#fff3cd', border: '1px solid #ffc107' }}>
                              <span style={{ fontSize: '0.85rem' }}>🔐</span>
                              <span style={{ fontSize: '0.72rem', color: '#856404', fontWeight: 500 }}>Insert your hardware key when prompted during sync</span>
                            </div>
                          )}
                        </div>
                      )}
                    </div>

                    <button
                      className="btn-pill btn-pill-blue mt-1"
                      style={{ width: '100%', justifyContent: 'center', padding: '0.65rem 1rem', fontSize: '0.875rem', borderRadius: '8px' }}
                      onClick={handleConnect}
                      disabled={connecting || syncing}
                    >
                      {connecting ? 'Saving...' : supplier?.activationState === 2 ? 'Save Configuration' : `Connect ${displayName}`}
                    </button>
                  </Form>
                )}

                {/* ── SSO ── */}
                {connType === 'sso' && (
                  <div>
                    {/* Org-wide SSOs */}
                    <div style={{ marginBottom: '1.25rem' }}>
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.6rem' }}>
                        <div style={{ fontSize: '0.7rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em', color: '#adb5bd' }}>
                          Organisation SSO
                        </div>
                        <button
                          type="button"
                          onClick={() => { setShowManualSso(v => !v); setSelectedOrgSso(null); }}
                          style={{ background: 'none', border: 'none', fontSize: '0.72rem', color: 'var(--color-primary, #5b8dee)', cursor: 'pointer', fontWeight: 600, padding: 0 }}
                        >
                          {showManualSso ? '← Use team SSO' : '+ Configure manually'}
                        </button>
                      </div>

                      {!showManualSso && (
                        <>
                          {orgSsoConfigs.length === 0 ? (
                            <div style={{ padding: '1rem', background: '#f8f9fa', borderRadius: '10px', border: '1px dashed #dee2e6', textAlign: 'center' }}>
                              <div style={{ fontSize: '0.78rem', color: '#adb5bd' }}>No team SSOs configured yet</div>
                              <button type="button" onClick={() => setShowManualSso(true)} style={{ background: 'none', border: 'none', fontSize: '0.72rem', color: 'var(--color-primary, #5b8dee)', cursor: 'pointer', fontWeight: 600, marginTop: '0.25rem' }}>
                                Configure one →
                              </button>
                            </div>
                          ) : (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                              {orgSsoConfigs.map(cfg => {
                                const sel = selectedOrgSso === cfg.id;
                                return (
                                  <button
                                    key={cfg.id}
                                    type="button"
                                    onClick={() => setSelectedOrgSso(sel ? null : cfg.id)}
                                    style={{
                                      display: 'flex', alignItems: 'center', gap: '0.75rem',
                                      padding: '0.7rem 0.9rem', borderRadius: '10px', cursor: 'pointer',
                                      border: sel ? '2px solid var(--color-primary, #5b8dee)' : '2px solid #e9ecef',
                                      background: sel ? '#f0f5ff' : '#f8f9fa',
                                      textAlign: 'left', width: '100%', transition: 'all 0.15s',
                                    }}
                                  >
                                    <div style={{
                                      width: 34, height: 34, borderRadius: '8px', background: '#fff',
                                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                                      border: '1px solid #e9ecef', flexShrink: 0,
                                    }}>
                                      <img src={`https://www.google.com/s2/favicons?domain=${cfg.providerDomain}&sz=32`} alt={cfg.provider} style={{ width: 18, height: 18, objectFit: 'contain' }} />
                                    </div>
                                    <div style={{ flex: 1, minWidth: 0 }}>
                                      <div style={{ fontSize: '0.8rem', fontWeight: 600, color: sel ? 'var(--color-primary, #5b8dee)' : '#343a40', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                        {cfg.name}
                                      </div>
                                      <div style={{ fontSize: '0.68rem', color: '#adb5bd', marginTop: '1px' }}>{cfg.domain} · {cfg.provider}</div>
                                    </div>
                                    <span style={{
                                      fontSize: '0.6rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em',
                                      padding: '2px 7px', borderRadius: '10px', flexShrink: 0,
                                      background: cfg.status === 'active' ? '#d1f5e0' : '#f0f0f0',
                                      color: cfg.status === 'active' ? '#1a7f47' : '#adb5bd',
                                    }}>
                                      {cfg.status}
                                    </span>
                                  </button>
                                );
                              })}
                            </div>
                          )}

                          {selectedOrgSso && (
                            <button
                              className="btn-pill btn-pill-blue"
                              style={{ width: '100%', justifyContent: 'center', padding: '0.65rem 1rem', fontSize: '0.875rem', borderRadius: '8px', marginTop: '0.75rem' }}
                              disabled
                            >
                              Use this SSO — Coming Soon
                            </button>
                          )}
                        </>
                      )}
                    </div>

                    {/* Manual SSO config */}
                    {showManualSso && (
                      <div>
                        <Form>
                          <FormGroup>
                            <Label style={{ fontSize: '0.7rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em', color: '#adb5bd' }}>Identity Provider</Label>
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.5rem', marginBottom: '0.25rem' }}>
                              {[
                                { name: 'Okta', domain: 'okta.com' },
                                { name: 'Azure AD', domain: 'microsoft.com' },
                                { name: 'Google', domain: 'workspace.google.com' },
                                { name: 'OneLogin', domain: 'onelogin.com' },
                                { name: 'Auth0', domain: 'auth0.com' },
                                { name: 'Custom', domain: 'openid.net' },
                              ].map(({ name, domain }) => {
                                const selected = ssoProvider === name;
                                return (
                                  <button key={name} type="button" onClick={() => setSsoProvider(name)} style={{
                                    display: 'flex', alignItems: 'center', gap: '0.5rem',
                                    padding: '0.55rem 0.75rem', borderRadius: '8px', cursor: 'pointer',
                                    border: selected ? '2px solid var(--color-primary, #5b8dee)' : '2px solid #e9ecef',
                                    background: selected ? '#f0f5ff' : '#f8f9fa',
                                    textAlign: 'left', width: '100%', transition: 'all 0.15s',
                                  }}>
                                    <img src={`https://www.google.com/s2/favicons?domain=${domain}&sz=32`} alt={name} style={{ width: 18, height: 18, objectFit: 'contain', flexShrink: 0 }} />
                                    <span style={{ fontSize: '0.75rem', fontWeight: 600, color: selected ? 'var(--color-primary, #5b8dee)' : '#343a40', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{name}</span>
                                  </button>
                                );
                              })}
                            </div>
                          </FormGroup>
                          <FormGroup>
                            <Label for="ssoDomain">Company domain</Label>
                            <Input type="text" id="ssoDomain" placeholder="e.g. yourcompany.com" />
                          </FormGroup>
                          <FormGroup>
                            <Label for="ssoMetadata">Metadata URL <span style={{ fontSize: '0.7rem', color: '#adb5bd', fontWeight: 400 }}>(optional)</span></Label>
                            <Input type="text" id="ssoMetadata" placeholder="https://..." />
                          </FormGroup>
                        </Form>
                        <div style={{ background: '#f0f5ff', borderRadius: '8px', padding: '0.75rem', marginBottom: '1rem', display: 'flex', gap: '0.6rem', alignItems: 'flex-start' }}>
                          <span style={{ fontSize: '0.9rem', flexShrink: 0 }}>ℹ️</span>
                          <p style={{ fontSize: '0.72rem', color: '#5b8dee', margin: 0, lineHeight: 1.5 }}>
                            Your IT admin will need to configure the SAML or OIDC integration on your identity provider side.
                          </p>
                        </div>
                        <button
                          className="btn-pill btn-pill-blue"
                          style={{ width: '100%', justifyContent: 'center', padding: '0.65rem 1rem', fontSize: '0.875rem', borderRadius: '8px' }}
                          disabled
                        >
                          SSO — Coming Soon
                        </button>
                      </div>
                    )}
                  </div>
                )}

                {/* ── Password Manager / Vault ── */}
                {connType === 'vault' && (
                  <div>
                    <div style={{ fontSize: '0.7rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em', color: '#adb5bd', marginBottom: '0.6rem' }}>
                      Select your vault
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.5rem', marginBottom: '1.25rem' }}>
                      {[
                        { name: '1Password', domain: '1password.com' },
                        { name: 'Bitwarden', domain: 'bitwarden.com' },
                        { name: 'LastPass', domain: 'lastpass.com' },
                        { name: 'Dashlane', domain: 'dashlane.com' },
                        { name: 'Keeper', domain: 'keepersecurity.com' },
                        { name: 'NordPass', domain: 'nordpass.com' },
                      ].map(({ name, domain }) => (
                        <button key={name} style={{
                          display: 'flex', alignItems: 'center', gap: '0.5rem',
                          padding: '0.6rem 0.75rem', borderRadius: '8px', cursor: 'pointer',
                          border: '2px solid #e9ecef', background: '#f8f9fa',
                          textAlign: 'left', width: '100%',
                          transition: 'border-color 0.15s',
                        }}
                        onMouseEnter={e => e.currentTarget.style.borderColor = '#5b8dee'}
                        onMouseLeave={e => e.currentTarget.style.borderColor = '#e9ecef'}
                        >
                          <img src={`https://www.google.com/s2/favicons?domain=${domain}&sz=32`} alt={name} style={{ width: 20, height: 20, objectFit: 'contain' }} />
                          <span style={{ fontSize: '0.78rem', fontWeight: 600, color: '#343a40' }}>{name}</span>
                        </button>
                      ))}
                    </div>
                    <div style={{ background: '#f0f5ff', borderRadius: '8px', padding: '0.75rem', marginBottom: '1rem', display: 'flex', gap: '0.6rem', alignItems: 'flex-start' }}>
                      <span style={{ fontSize: '0.9rem', flexShrink: 0 }}>ℹ️</span>
                      <p style={{ fontSize: '0.72rem', color: '#5b8dee', margin: 0, lineHeight: 1.5 }}>
                        BillsMax will request read-only access to retrieve credentials for this supplier. We never store your vault master password.
                      </p>
                    </div>
                    <button
                      className="btn-pill btn-pill-blue"
                      style={{ width: '100%', justifyContent: 'center', padding: '0.65rem 1rem', fontSize: '0.875rem', borderRadius: '8px' }}
                      disabled
                    >
                      Vault Connect — Coming Soon
                    </button>
                  </div>
                )}

                {/* Divider */}
                <div style={{ height: 1, background: '#f0f0f0', margin: '1.5rem 0 1.25rem' }} />

                {/* ── Sync Schedule ── */}
                <div style={{ marginBottom: '1.5rem' }}>
                  <div style={{ fontSize: '0.7rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em', color: '#adb5bd', marginBottom: '0.6rem' }}>
                    Sync Schedule
                  </div>
                  <div style={{ fontSize: '0.72rem', color: '#6c757d', marginBottom: '0.85rem' }}>
                    How often should we pull data from {displayName}?
                  </div>

                  {/* Mode toggle */}
                  <div style={{ display: 'flex', gap: '0.4rem', marginBottom: '0.85rem', padding: '0.25rem', background: '#f8f9fa', borderRadius: '8px', border: '1px solid #e9ecef' }}>
                    {[
                      { key: 'simple', label: 'Simple' },
                      { key: 'cron', label: 'Cron' },
                    ].map(({ key, label }) => (
                      <button
                        key={key}
                        type="button"
                        onClick={() => setSyncMode(key)}
                        style={{
                          flex: 1, padding: '0.4rem 0.75rem', borderRadius: '6px',
                          border: 'none', cursor: 'pointer',
                          background: syncMode === key ? '#fff' : 'transparent',
                          boxShadow: syncMode === key ? '0 1px 2px rgba(0,0,0,0.06)' : 'none',
                          fontSize: '0.75rem', fontWeight: 600,
                          color: syncMode === key ? 'var(--color-primary, #5b8dee)' : '#6c757d',
                          transition: 'all 0.15s',
                        }}
                      >
                        {label}
                      </button>
                    ))}
                  </div>

                  {syncMode === 'simple' && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                      <span style={{ fontSize: '0.82rem', color: '#495057' }}>Every</span>
                      <Input
                        type="number"
                        min={1}
                        value={syncInterval}
                        onChange={e => setSyncInterval(Math.max(1, parseInt(e.target.value, 10) || 1))}
                        style={{ width: 80, textAlign: 'center', fontSize: '0.82rem', padding: '0.45rem 0.6rem', border: '1px solid #e9ecef', borderRadius: '8px' }}
                      />
                      <Input
                        type="select"
                        value={syncUnit}
                        onChange={e => setSyncUnit(e.target.value)}
                        style={{
                          flex: 1,
                          fontSize: '0.82rem',
                          fontWeight: 500,
                          color: '#495057',
                          padding: '0.45rem 2rem 0.45rem 0.75rem',
                          border: '1px solid #e9ecef',
                          borderRadius: '8px',
                          background: `#fff url("data:image/svg+xml;charset=utf-8,%3Csvg xmlns='http://www.w3.org/2000/svg' width='10' height='6' viewBox='0 0 10 6'%3E%3Cpath fill='none' stroke='%236c757d' stroke-width='1.5' stroke-linecap='round' stroke-linejoin='round' d='M1 1l4 4 4-4'/%3E%3C/svg%3E") no-repeat right 0.75rem center`,
                          backgroundSize: '10px 6px',
                          appearance: 'none',
                          WebkitAppearance: 'none',
                          MozAppearance: 'none',
                          cursor: 'pointer',
                        }}
                      >
                        <option value="days">{syncInterval === 1 ? 'Day' : 'Days'}</option>
                        <option value="weeks">{syncInterval === 1 ? 'Week' : 'Weeks'}</option>
                        <option value="months">{syncInterval === 1 ? 'Month' : 'Months'}</option>
                      </Input>
                    </div>
                  )}

                  {syncMode === 'cron' && (
                    <FormGroup style={{ marginBottom: 0 }}>
                      <Input
                        type="text"
                        placeholder="0 9 * * MON"
                        value={syncCron}
                        onChange={e => setSyncCron(e.target.value)}
                        style={{ fontFamily: 'monospace', fontSize: '0.82rem' }}
                      />
                      <div style={{ fontSize: '0.68rem', color: '#adb5bd', marginTop: '0.35rem' }}>
                        Standard cron expression — minute hour day month weekday
                      </div>
                    </FormGroup>
                  )}
                </div>
              </div>
            )}

            {/* ── WORKFLOW ── */}
            {panelTab === 'workflow' && (
              <div>
                <div style={{ fontSize: '0.7rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em', color: '#adb5bd', marginBottom: '0.9rem' }}>
                  Automation Rules
                </div>

                {/* Trigger steps */}
                {[
                  {
                    icon: '📥',
                    label: 'On Invoice Received',
                    desc: 'Trigger when a new invoice is pulled from this supplier',
                    enabled: true,
                  },
                  {
                    icon: '✅',
                    label: 'Auto-approve under threshold',
                    desc: 'Automatically approve invoices below a set amount',
                    enabled: false,
                  },
                  {
                    icon: '👤',
                    label: 'Assign to member',
                    desc: 'Route new invoices to a specific team member for review',
                    enabled: false,
                  },
                  {
                    icon: '🔔',
                    label: 'Notify on failure',
                    desc: 'Send an alert when a sync fails',
                    enabled: true,
                  },
                  {
                    icon: '📤',
                    label: 'Export to accounting',
                    desc: 'Push approved invoices to your accounting software',
                    enabled: false,
                  },
                ].map(({ icon, label, desc, enabled }, i) => (
                  <div key={i} style={{
                    display: 'flex', alignItems: 'center', gap: '0.75rem',
                    padding: '0.8rem 0.9rem', borderRadius: '10px', marginBottom: '0.5rem',
                    border: `1px solid ${enabled ? '#c8e6ff' : '#e9ecef'}`,
                    background: enabled ? '#f0f5ff' : '#f8f9fa',
                  }}>
                    <span style={{ fontSize: '1.2rem', flexShrink: 0 }}>{icon}</span>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: '0.8rem', fontWeight: 600, color: enabled ? 'var(--color-primary, #5b8dee)' : '#343a40' }}>{label}</div>
                      <div style={{ fontSize: '0.68rem', color: '#adb5bd', marginTop: '1px' }}>{desc}</div>
                    </div>
                    {/* Toggle */}
                    <div style={{
                      width: 36, height: 20, borderRadius: 10, flexShrink: 0, cursor: 'pointer',
                      background: enabled ? 'var(--color-primary, #5b8dee)' : '#dee2e6',
                      position: 'relative', transition: 'background 0.2s',
                    }}>
                      <div style={{
                        width: 14, height: 14, borderRadius: '50%', background: '#fff',
                        position: 'absolute', top: 3,
                        left: enabled ? 19 : 3,
                        transition: 'left 0.2s',
                        boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
                      }} />
                    </div>
                  </div>
                ))}

                <div style={{ marginTop: '1.25rem', padding: '0.75rem', background: '#f8f9fa', borderRadius: '10px', border: '1px dashed #dee2e6', textAlign: 'center' }}>
                  <div style={{ fontSize: '0.72rem', color: '#adb5bd', marginBottom: '0.4rem' }}>Custom workflows coming soon</div>
                  <div style={{ fontSize: '0.68rem', color: '#ced4da' }}>Build multi-step automations with conditions and actions</div>
                </div>
              </div>
            )}

          </div>
        </div>
      );
    })()}
    
    {/* Backdrop when sidemenu is open */}
    {selectedSupplierId != null && (
      <div
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          zIndex: 1040
        }}
        onClick={() => {
          setSelectedSupplierId(null);
          setEmail('');
          setPassword('');
          setConnectError(null);
        }}
      />
    )}
    
    {/* Toast notifications */}
    <div style={{ position: 'fixed', bottom: '1.5rem', right: '1.5rem', zIndex: 2000 }}>
      <Toast isOpen={!!toast}>
        <ToastBody style={{ backgroundColor: toast?.type === 'danger' ? '#f8d7da' : '#d1e7dd', color: toast?.type === 'danger' ? '#842029' : '#0f5132' }}>
          {toast?.message}
        </ToastBody>
      </Toast>
    </div>

    {/* Coming Soon Modal */}
    <Modal isOpen={showComingSoonModal} toggle={() => setShowComingSoonModal(false)} centered>
      <ModalHeader toggle={() => setShowComingSoonModal(false)}>
        {requestedSupplier ? `${requestedSupplier.title} is coming soon` : 'Add your own supplier'}
      </ModalHeader>
      <ModalBody>
        {requestedSupplier && (
          <p>
            BillsMax doesn't support {requestedSupplier.title} yet. Request it and we'll prioritise the suppliers people
            ask for most, or set it up yourself below.
          </p>
        )}
        <div style={{ fontWeight: 600, fontSize: '0.85rem', marginBottom: '0.5rem' }}>Set it up yourself</div>
        <p style={{ fontSize: '0.8rem', color: '#6c757d' }}>
          Give the supplier's login page and BillsMax signs in there to look for invoices. It works for most sites with an
          email and password login.
        </p>
        <Input className="mb-2" placeholder="Supplier name" value={customName} onChange={e => setCustomName(e.target.value)} />
        <Input placeholder="https://supplier.com/login" value={customLoginUrl} onChange={e => setCustomLoginUrl(e.target.value)} />
        {customError && <div className="text-danger mt-2" style={{ fontSize: '0.8rem' }}>{customError}</div>}
      </ModalBody>
      <ModalFooter>
        <Button color="secondary" outline onClick={() => setShowComingSoonModal(false)}>
          Close
        </Button>
        {requestedSupplier && (
          <Button color="secondary" disabled={requesting} onClick={handleRequestSupplier}>
            {requesting ? 'Requesting…' : `Request ${requestedSupplier.title}`}
          </Button>
        )}
        <Button color="primary" disabled={addingCustom || !customName || !customLoginUrl} onClick={handleAddCustom}>
          {addingCustom ? 'Adding…' : 'Set it up'}
        </Button>
      </ModalFooter>
    </Modal>
    </>
  );
};

export default Suppliers;

